import { createElement, FunctionComponent, ReactPortal } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const root = document.getElementById('root');

export type ComponentProps = {
  [key: string]: any;
};

/**
 * @function useGenerateImageFromCharts
 * @description Хук для получения PNG-изображения компонента, использующего Chart.js.
 *
 * @example
 * const [portal, uriString] = useGenerateImageFromCharts(YourComponent, props);
 *
 * @param {FunctionComponent} Component - React-компонент, который рендерит график.
 * @param {ComponentProps} [props={}] - Дополнительные пропсы для компонента.
 *
 * @returns {[ReactPortal | null, string]} Массив из двух элементов:
 * - `portal` – портал для монтирования в DOM.
 * - `uriString` – строка с URI полученного PNG-изображения.
 *
 * @usage
 * - Портал необходимо вмонтировать в React-дерево, передав `{portal}`.
 * - `uriString` можно использовать в PDF-компоненте через `<Image src={chartExampleImgSrc} />`.
 *
 * @chartjs
 * В компоненте с Chart.js необходимо добавить:
 * - `handleOnComplete` в пропсы.
 * - В `options` добавить:
 *   ```js
 *   animation: {
 *     duration: 0,
 *     onComplete(event) {
 *       handleOnComplete(event.chart.toBase64Image());
 *     }
 *   }
 *   ```
 */
export const useGenerateImageFromCharts = (
  Component: FunctionComponent,
  props: ComponentProps = {},
): [ReactPortal | null, string] => {
  const [element, setElement] = useState<HTMLElement | null>();
  const [imgSrc, setImgSrc] = useState<string>('');

  useEffect(() => {
    const div = document.createElement('div');
    div.id = Date.now().toString();
    div.className = 'pdf-charts-draw-container';
    root?.appendChild(div);
    setElement(div);
  }, []);

  useEffect(() => {
    if (element?.children[0]) {
      root?.removeChild(element);
      setElement(null);
    }
  }, [element]);

  return [
    element
      ? createPortal(createElement<ComponentProps>(Component, { ...props, handleOnComplete: setImgSrc }), element)
      : null,
    imgSrc,
  ];
};
